import { Lock } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSiteBranding } from "@/context/SiteContext";
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AGENT_ANALYTICS_SHELL_STYLE } from "@/lib/agentVisualTokens";
import { trackLeadIntent } from "@/lib/leadIntent";
import { formatCurrency } from "@/lib/portalHelpers";

const SCENARIO_PRESETS = {
  conservative: { label: "Conservative", appreciationRatePct: 1.0, mortgageRatePct: 4.2, vacancyMonthsPerYear: 1.0 },
  base: { label: "Base Case", appreciationRatePct: 3.5, mortgageRatePct: 3.0, vacancyMonthsPerYear: 0.5 },
  optimistic: { label: "Optimistic", appreciationRatePct: 5.5, mortgageRatePct: 2.4, vacancyMonthsPerYear: 0.0 },
};

export const ExitStrategyModeler = ({ listing, forecast, isAgentTheme = false, onRequirePremium, premiumLead }) => {
  const { requestParams } = useSiteBranding();
  const [selectedScenario, setSelectedScenario] = useState("base");
  const [inputs, setInputs] = useState({
    purchaseDate: new Date().toISOString().slice(0, 10),
    listingPrice: Number(listing?.price || forecast?.current_estimated_value || 0),
    estimatedRentalYieldPct: 0,
    mortgageRatePct: 3.0,
    appreciationRatePct: Number(forecast?.annual_projection_growth_pct || 4.5),
    propertyTaxMode: "investment",
    buyerProfile: "SC",
    propertyCount: 1,
  });
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUnlockHint, setShowUnlockHint] = useState(false);
  const [exporting, setExporting] = useState(false);
  const trackedCockpitOpenRef = useRef("");
  const chartContainerRef = useRef(null);
  const [chartContainerWidth, setChartContainerWidth] = useState(0);

  const hasPremiumAccess = useMemo(() => Boolean(premiumLead?.email), [premiumLead]);

  useEffect(() => {
    if (!premiumLead?.email || !listing?.id) return;
    const key = `${listing.id}:${premiumLead.email}`;
    if (trackedCockpitOpenRef.current === key) return;
    trackedCockpitOpenRef.current = key;
    trackLeadIntent({ premiumLead, eventType: "cockpit_open", listing });
  }, [listing?.id, premiumLead?.email]);

  useEffect(() => {
    if (!chartContainerRef.current) return undefined;
    const resizeObserver = new ResizeObserver((entries) => {
      const entryWidth = Math.max(0, Math.floor(entries[0]?.contentRect?.width || 0));
      setChartContainerWidth(entryWidth);
    });
    resizeObserver.observe(chartContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const preset = SCENARIO_PRESETS[selectedScenario];
    setInputs((current) => ({
      ...current,
      appreciationRatePct: preset.appreciationRatePct,
      mortgageRatePct: preset.mortgageRatePct,
    }));
  }, [selectedScenario]);

  useEffect(() => {
    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.post(`/listings/${listing.id}/exit-strategy-model`, {
          purchase_date: inputs.purchaseDate,
          listing_price: Number(inputs.listingPrice || 0),
          estimated_rental_yield_pct: Number(inputs.estimatedRentalYieldPct || 0),
          interest_rate_pct: Number(inputs.mortgageRatePct || 0),
          annual_capital_appreciation_rate_pct: Number(inputs.appreciationRatePct || 0),
          property_tax_mode: inputs.propertyTaxMode,
          buyer_profile: inputs.buyerProfile,
          property_count: Number(inputs.propertyCount || 1),
          vacancy_months_per_year: SCENARIO_PRESETS[selectedScenario].vacancyMonthsPerYear,
          scenario_label: SCENARIO_PRESETS[selectedScenario].label,
        }, { params: requestParams });
        setModel(response.data);
        setInputs((current) => ({
          ...current,
          estimatedRentalYieldPct: current.estimatedRentalYieldPct || Number(response.data.inputs.estimated_rental_yield_pct || 0),
        }));
      } catch (requestError) {
        setError(formatApiErrorDetail(requestError.response?.data?.detail || "Unable to build the exit strategy model right now."));
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [inputs.appreciationRatePct, inputs.buyerProfile, inputs.estimatedRentalYieldPct, inputs.listingPrice, inputs.mortgageRatePct, inputs.propertyCount, inputs.propertyTaxMode, inputs.purchaseDate, listing.id, requestParams, selectedScenario]);

  const updateNumberInput = (key, value) => setInputs((current) => ({ ...current, [key]: Number(value || 0) }));
  const metricCards = model ? [
    { label: "Monthly Mortgage", value: formatCurrency(model.assumptions.monthly_mortgage_payment, 0) },
    { label: "Annual Net Cash Flow", value: formatCurrency(model.assumptions.annual_net_cash_flow, 0) },
    { label: "BSD", value: formatCurrency(model.inputs.bsd, 0) },
    { label: "Cash Outlay", value: formatCurrency(model.assumptions.cash_outlay, 0) },
    { label: "ABSD", value: formatCurrency(model.inputs.absd, 0) },
  ] : [];
  const pointByYear = useMemo(() => Object.fromEntries((model?.chart_points || []).map((point) => [point.year, point])), [model]);
  const textFillStyle = isAgentTheme ? { color: "#F2ECE3", WebkitTextFillColor: "#F2ECE3" } : undefined;
  const goldTextStyle = isAgentTheme ? { color: "#D4AF37", WebkitTextFillColor: "#D4AF37" } : undefined;
  const strongTextStyle = isAgentTheme ? { color: "#F9F8F6", WebkitTextFillColor: "#F9F8F6" } : undefined;
  const shellClass = isAgentTheme
    ? "editorial-dark-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8"
    : "rounded-[30px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-6 text-[#2C2C2C] shadow-[0_18px_60px_rgba(90,75,53,0.07)] md:p-8";
  const titleClass = isAgentTheme ? "text-[#F9F8F6]" : "text-[#001935]";
  const bodyClass = isAgentTheme ? "text-[#F2ECE3]" : "text-[#42566f]";
  const metricLabelClass = isAgentTheme
    ? "text-[9px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]"
    : "text-[11px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]";
  const metricValueClass = isAgentTheme
    ? "mt-3 break-words text-[clamp(0.78rem,0.8vw,1rem)] font-semibold leading-[1.04] tracking-[-0.03em]"
    : "mt-3 break-words text-[clamp(1.2rem,1.38vw,1.72rem)] font-semibold leading-[1.16]";
  const badgeClass = isAgentTheme ? "rounded-full border border-[#D4AF37]/30 bg-[#2A2110] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]" : "rounded-full border border-[#D4AF37]/30 bg-[#fff4dd] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]";
  const toggleActiveClass = isAgentTheme ? "border-[#D4AF37] bg-[#2A2110] text-[#D4AF37]" : "border-[#D4AF37] bg-[#fff4dd] text-[#8b6d16]";
  const toggleIdleClass = isAgentTheme ? "border-white/12 bg-[#121212] text-[#DDD6C9]" : "border-[#001935]/10 bg-white text-[#42566f]";
  const softCardClass = isAgentTheme ? "editorial-dark-soft rounded-[24px] border border-white/10 px-4 py-4" : "rounded-[24px] border border-[#D4AF37]/16 bg-white/88 px-4 py-4";
  const inputClass = isAgentTheme ? "mt-3 w-full rounded-2xl border border-[#D4AF37]/20 bg-[#1A1A1A] px-4 py-3 text-sm font-semibold text-[#F9F8F6] outline-none transition focus:border-[#D4AF37]" : "mt-3 w-full rounded-2xl border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm font-semibold text-[#001935] outline-none transition focus:border-[#D4AF37]";
  const chartShellClass = isAgentTheme ? "editorial-dark-soft rounded-[28px] border border-white/10 p-4 md:p-6" : "rounded-[28px] border border-[#D4AF37]/18 bg-white/88 p-4 shadow-[0_16px_45px_rgba(90,75,53,0.06)] md:p-6";
  const frictionPanelClass = isAgentTheme ? "editorial-dark-soft rounded-[28px] border border-[#D4AF37]/25 p-5" : "rounded-[28px] border border-[#D4AF37]/18 bg-[#fffaf1] p-5";
  const innerMetricCardClass = isAgentTheme ? "rounded-[22px] border border-white/10 bg-[#1A1A1A] px-4 py-4" : "rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4 min-h-[140px]";
  const exitChipClass = isAgentTheme ? "rounded-[16px] bg-[#121212] px-3 py-3" : "rounded-[16px] bg-[#fffaf1] px-3 py-3";
  const summaryCardClass = isAgentTheme ? "editorial-dark-soft rounded-[26px] border border-[#D4AF37]/25 p-5" : "rounded-[26px] border border-[#D4AF37]/18 bg-white/88 p-5";
  const chartGridColor = isAgentTheme ? "rgba(255,255,255,0.08)" : "rgba(0,25,53,0.08)";
  const chartAxisColor = isAgentTheme ? "#DDD6C9" : "#5D584F";
  const tooltipBackground = isAgentTheme ? "#1A1A1A" : "#fffaf1";
  const tooltipText = isAgentTheme ? "#F9F8F6" : "#001935";
  const eyebrow = isAgentTheme ? "Scenario Planner" : "Automation #11";
  const heading = isAgentTheme ? "Holding cost and resale planner" : "Interactive Exit Strategy & Cash Flow Modeler";
  const summary = isAgentTheme
    ? "Stress-test what this home could look like over 3, 5, and 10 years using editable Singapore-specific assumptions."
    : "Stress-test the path to profit over 3, 5, and 10 years using editable assumptions built for Singapore buyer economics.";
  const memoButtonLabel = exporting
    ? (isAgentTheme ? "Preparing Planner PDF..." : "Generating Exit Memo...")
    : (isAgentTheme ? "Download Planner PDF" : "Generate Exit Memo");
  const unlockHeading = isAgentTheme ? "Unlock the full planner" : "Unlock Premium Projections";
  const unlockCopy = isAgentTheme
    ? "The stronger-demand scenario and planner PDF are unlocked through the existing lead-capture step so your numbers can be saved and exported."
    : "The Optimistic scenario and Exit Memo PDF are premium investor tools. Unlock them through the existing lead-capture session to store your projections and export the memo.";
  const unlockButtonLabel = isAgentTheme ? "Unlock Full Planner" : "Unlock Premium Projections";
  const loadingSummaryText = isAgentTheme ? "Refreshing the planning view..." : "Refreshing the Alpha Investment Formula...";

  const triggerPremiumUnlock = () => {
    setShowUnlockHint(true);
    onRequirePremium?.();
  };

  const handleScenarioSelect = (scenarioKey) => {
    if (scenarioKey === "optimistic" && !hasPremiumAccess) {
      triggerPremiumUnlock();
      return;
    }
    setSelectedScenario(scenarioKey);
    trackLeadIntent({
      premiumLead,
      eventType: "scenario_switch",
      listing,
      metadata: {
        scenario_label: SCENARIO_PRESETS[scenarioKey].label,
        buyer_profile: inputs.buyerProfile,
      },
    });
  };

  const handleGenerateExitMemo = async () => {
    if (!hasPremiumAccess || !premiumLead) {
      triggerPremiumUnlock();
      return;
    }
    try {
      setExporting(true);
      const response = await api.post(
        `/listings/${listing.id}/exit-memo-report`,
        {
          name: premiumLead.name,
          phone: premiumLead.phone || premiumLead.whatsapp,
          email: premiumLead.email,
          purchase_date: inputs.purchaseDate,
          listing_price: Number(inputs.listingPrice || 0),
          estimated_rental_yield_pct: Number(inputs.estimatedRentalYieldPct || 0),
          interest_rate_pct: Number(inputs.mortgageRatePct || 0),
          annual_capital_appreciation_rate_pct: Number(inputs.appreciationRatePct || 0),
          property_tax_mode: inputs.propertyTaxMode,
          buyer_profile: inputs.buyerProfile,
          property_count: Number(inputs.propertyCount || 1),
          vacancy_months_per_year: SCENARIO_PRESETS[selectedScenario].vacancyMonthsPerYear,
          scenario_label: SCENARIO_PRESETS[selectedScenario].label,
        },
        { params: requestParams, responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `SG-Prop-Portal-Exit-Memo-${listing.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      await trackLeadIntent({
        premiumLead,
        eventType: "pdf_download",
        listing,
        metadata: {
          pdf_type: "exit_memo",
          district: listing.district,
          scenario_label: SCENARIO_PRESETS[selectedScenario].label,
        },
      });
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail || "Unable to generate the exit memo right now."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className={shellClass} data-testid="listing-detail-exit-strategy-modeler" style={isAgentTheme ? { ...AGENT_ANALYTICS_SHELL_STYLE, ...textFillStyle } : textFillStyle}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]" style={goldTextStyle}>{eyebrow}</p>
          <h2 className={`mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold ${titleClass}`}>{heading}</h2>
          <p className={`mt-4 text-sm leading-8 ${bodyClass}`} style={textFillStyle}>{summary}</p>
        </div>
        <div className={badgeClass} data-testid="listing-detail-exit-strategy-yield-disclaimer" style={goldTextStyle}>
          {model?.yield_disclaimer || "Estimated based on District averages."}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="listing-detail-exit-scenario-toggle-group">
          {Object.entries(SCENARIO_PRESETS).map(([key, preset]) => {
            const isLocked = key === "optimistic" && !hasPremiumAccess;
            const isActive = selectedScenario === key;
            return (
              <button
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${isActive ? toggleActiveClass : toggleIdleClass} ${isLocked ? "opacity-70" : ""}`}
                data-testid={`listing-detail-exit-scenario-${key}`}
                key={key}
                onClick={() => handleScenarioSelect(key)}
                type="button"
              >
                {isLocked ? <Lock className="mr-2 inline h-3.5 w-3.5" /> : null}
                {preset.label}
              </button>
            );
          })}
        </div>
        <button
          className={`gold-button !px-4 !py-2 !text-xs !tracking-[0.18em] ${!hasPremiumAccess ? "blur-[0.3px]" : ""}`}
          data-testid="listing-detail-exit-memo-button"
          onClick={handleGenerateExitMemo}
          type="button"
        >
          {memoButtonLabel}
        </button>
      </div>

      {model ? (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="listing-detail-exit-rule-hints">
          <div className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${toggleIdleClass}`} data-testid="listing-detail-exit-absd-hint">
            {model.absd_rule_text}
          </div>
          <div className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${toggleActiveClass}`} data-testid="listing-detail-exit-ssd-hint">
            {model.ssd_rule_text}
          </div>
        </div>
      ) : null}

      {showUnlockHint && !hasPremiumAccess ? (
        <div className={softCardClass} data-testid="listing-detail-exit-premium-unlock-card">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]" style={goldTextStyle}>{unlockHeading}</p>
          <p className={`mt-3 text-sm leading-8 ${bodyClass}`} style={textFillStyle}>{unlockCopy}</p>
          <button className="gold-button mt-4 !px-4 !py-2 !text-xs !tracking-[0.18em]" data-testid="listing-detail-exit-premium-unlock-button" onClick={triggerPremiumUnlock} type="button">
            {unlockButtonLabel}
          </button>
        </div>
      ) : null}

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {[
          { key: "purchaseDate", label: "Purchase Date", step: null, type: "date" },
          { key: "listingPrice", label: "Listing Price", step: "10000" },
          { key: "estimatedRentalYieldPct", label: "Rental Yield %", step: "0.1" },
          { key: "mortgageRatePct", label: "Mortgage Rate %", step: "0.1" },
          { key: "appreciationRatePct", label: "Appreciation %", step: "0.1" },
        ].map((field) => (
          <label className={softCardClass} data-testid={`listing-detail-exit-input-${field.key}`} key={field.key}>
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>{field.label}</span>
            <input className={inputClass} min={field.type === "date" ? undefined : "0"} onChange={(event) => field.type === "date" ? setInputs((current) => ({ ...current, [field.key]: event.target.value })) : updateNumberInput(field.key, event.target.value)} step={field.step || undefined} type={field.type || "number"} value={inputs[field.key]} />
          </label>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="listing-detail-exit-property-tax-toggle-group">
        {[
          { label: "Investment Tax Mode", value: "investment" },
          { label: "Owner-Occupied Tax Mode", value: "owner_occupied" },
        ].map((option) => (
          <button
            className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${inputs.propertyTaxMode === option.value ? toggleActiveClass : toggleIdleClass}`}
            data-testid={`listing-detail-exit-property-tax-toggle-${option.value}`}
            key={option.value}
            onClick={() => setInputs((current) => ({ ...current, propertyTaxMode: option.value }))}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="listing-detail-exit-buyer-profile-group">
        <label className={softCardClass} data-testid="listing-detail-exit-buyer-profile-input">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>Buyer Profile</span>
          <select className={inputClass} onChange={(event) => {
            const nextProfile = event.target.value;
            setInputs((current) => ({ ...current, buyerProfile: nextProfile }));
            trackLeadIntent({ premiumLead, eventType: "tax_profile_model", listing, metadata: { buyer_profile: nextProfile, property_count: inputs.propertyCount } });
          }} value={inputs.buyerProfile}>
            <option value="SC">SC (Singapore Citizen)</option>
            <option value="PR">PR (Permanent Resident)</option>
            <option value="Foreigner">Foreigner</option>
            <option value="Entity / Trust">Entity / Trust</option>
          </select>
        </label>
        <label className={softCardClass} data-testid="listing-detail-exit-property-count-input">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>Property Count</span>
          <select className={inputClass} onChange={(event) => {
            const nextCount = Number(event.target.value);
            setInputs((current) => ({ ...current, propertyCount: nextCount }));
            trackLeadIntent({ premiumLead, eventType: "tax_profile_model", listing, metadata: { buyer_profile: inputs.buyerProfile, property_count: nextCount } });
          }} value={inputs.propertyCount}>
            <option value={1}>1st Property</option>
            <option value={2}>2nd Property</option>
            <option value={3}>3rd Property+</option>
          </select>
        </label>
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <div className={chartShellClass} data-testid="listing-detail-exit-strategy-chart-card">
          {error ? <p className="mb-4 text-sm text-red-600" data-testid="listing-detail-exit-strategy-error">{error}</p> : null}
          <div className="h-[320px] w-full" ref={chartContainerRef}>
            {chartContainerWidth > 0 ? <AreaChart data={model?.chart_points || []} height={320} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} width={chartContainerWidth}>
                <defs>
                  <linearGradient id="profitFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.36} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chartGridColor} strokeDasharray="4 4" />
                <XAxis dataKey="year" stroke={chartAxisColor} tick={{ fill: chartAxisColor }} tickLine={false} />
                <YAxis stroke={chartAxisColor} tick={{ fill: chartAxisColor }} tickFormatter={(value) => `${Math.round(value / 1000)}k`} tickLine={false} width={72} />
                <Tooltip contentStyle={{ borderRadius: 18, border: "1px solid rgba(212,175,55,0.25)", background: tooltipBackground, color: tooltipText }} formatter={(value) => formatCurrency(value, 0)} labelFormatter={(label) => `${label} Years`} />
                <Area dataKey="net_exit_profit" fill="url(#profitFill)" name="Net Exit Profit" stroke="#D4AF37" strokeWidth={3} type="monotone" />
                <Area dataKey="cumulative_net_cash_flow" fill="rgba(0,25,53,0.08)" name="Cumulative Net Cash Flow" stroke="#001935" strokeWidth={2} type="monotone" />
              </AreaChart> : <div className={`flex h-full items-center justify-center rounded-[22px] border text-sm ${isAgentTheme ? "border-white/10 bg-[#121212] text-[#DDD6C9]" : "border-[#001935]/10 bg-[#fffaf1] text-[#42566f]"}`} data-testid="listing-detail-exit-strategy-chart-loading">Preparing the exit model...</div>}
          </div>
        </div>

        <div className={frictionPanelClass} data-testid="listing-detail-tax-friction-panel" style={textFillStyle}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]" style={goldTextStyle}>Tax & Friction</p>
          <div className="mt-4 grid gap-4">
            <div className={innerMetricCardClass} data-testid="listing-detail-tax-friction-acquisition" style={textFillStyle}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>Acquisition Costs</p>
              <div className={`mt-3 grid gap-2 text-sm ${bodyClass}`}>
                <p>BSD: {formatCurrency(model?.inputs?.bsd || 0, 0)}</p>
                <p>ABSD: {formatCurrency(model?.inputs?.absd || 0, 0)}</p>
                <p>Legal: {formatCurrency(model?.inputs?.buy_legal_fee || 0, 0)}</p>
              </div>
            </div>
            <div className={innerMetricCardClass} data-testid="listing-detail-tax-friction-holding" style={textFillStyle}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>Holding Costs</p>
              <div className={`mt-3 grid gap-2 text-sm ${bodyClass}`}>
                <p>Property Tax: {formatCurrency(model?.assumptions?.property_tax || 0, 0)}</p>
                <p>Maintenance: {formatCurrency((model?.inputs?.maintenance_fee_monthly || 0) * 12, 0)} / yr</p>
              </div>
            </div>
            <div className={innerMetricCardClass} data-testid="listing-detail-tax-friction-exit" style={textFillStyle}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]" style={goldTextStyle}>Exit Frictions</p>
              <div className={`mt-3 grid gap-3 text-sm ${bodyClass}`}>
                {[3, 5, 10].map((year) => (
                  <div className={exitChipClass} data-testid={`listing-detail-tax-friction-exit-${year}y`} key={year} style={textFillStyle}>
                    <p className={`font-semibold ${titleClass}`} style={strongTextStyle}>{year}-Year Exit</p>
                    <p>SSD: {formatCurrency(pointByYear[year]?.ssd || 0, 0)}</p>
                    <p>Agent Comm: {formatCurrency((pointByYear[year]?.selling_costs || 0) - (model?.inputs?.selling_legal_fee || 0), 0)}</p>
                    <p>Legal: {formatCurrency(model?.inputs?.selling_legal_fee || 0, 0)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <div className={summaryCardClass} data-testid="listing-detail-exit-strategy-summary-card" style={textFillStyle}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]" style={goldTextStyle}>Clear Path to Profit</p>
          <p className={`mt-4 text-sm leading-8 ${bodyClass}`} data-testid="listing-detail-exit-strategy-summary-text" style={textFillStyle}>{loading ? loadingSummaryText : model?.summary_text}</p>
        </div>
        <div className={`grid gap-4 ${isAgentTheme ? "md:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-2 xl:grid-cols-2"}`}>
    {metricCards.map((item) => (
            <div className={innerMetricCardClass} data-testid={`listing-detail-exit-strategy-metric-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label} style={textFillStyle}>
              <p className={metricLabelClass} style={goldTextStyle}>{item.label}</p>
              <p className={`${metricValueClass} ${titleClass}`} style={strongTextStyle}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};